import { useState, useEffect, useMemo } from 'react';
import type { ZprToggleCustomEvent } from '@zappar/zpr-ui-components-stencil';
import { IAsset } from '../providers';
import { useAssetActions } from '../context/AssetActionsContext';
import { useEchoContext } from '../echo/EchoContext';
import { publicLinkCreatedBody } from '../echo/systemMessages';
import { Client, IPublicLink } from '@zappar/zw-api-client';
import { ZprToggle } from '@zpr-stencil';
import { formatDate, slugify } from '../utils';
import { FailIcon, SuccessIcon, ProcessingIcon } from '../../../icons';
import { MediaType } from '../types';
import { InlineInput } from '../..';

type SizeOption = {
  id: string;
  label: string;
  value: string | boolean;
};

type EmbedQuerystring = {
  experience?: string;
  width?: string;
  height?: string;
  pan?: boolean;
  zoom?: boolean;
  rotateHorizontal?: boolean;
  rotateVertical?: boolean;
  scale?: boolean;
};

type EmbedTemplate = {
  label: string;
  value: string;
  mediaTypes?: MediaType[];
  buildUrl: (backendUrl: string, queryParams?: EmbedQuerystring) => string;
  embedOptions: SizeOption[];
};

function extractFolder(url: string): string {
  const segments = new URL(url).pathname.split('/').filter(Boolean);
  return segments.slice(0, -1).join('/');
}

function generateQueryStringFromObject(obj: Record<string, string | boolean>): string {
  const params = new URLSearchParams();
  for (const key in obj) {
    params.append(key, String(obj[key]));
  }
  console.log(params.toString());
  return params.toString();
}

export const EMBED_TEMPLATES: EmbedTemplate[] = [
  {
    label: 'File only',
    value: 'file',
    mediaTypes: Object.values(MediaType).filter(t => t !== MediaType.Model3D),
    buildUrl: (url) => url,
    embedOptions: [
      { id: 'width', label: 'Width', value: '640' },
      { id: 'height', label: 'Height', value: '480' },
    ],
  },
  {
    label: '3D Viewer',
    value: 'basic-3d-viewer',
    mediaTypes: [MediaType.Model3D],
    buildUrl: (url, queryParams) => `https://7lgrv.zappar-us.io/viewer/?key=${extractFolder(url)}&${generateQueryStringFromObject(queryParams ?? {})}`,
    embedOptions: [
      { id: 'width', label: 'Width', value: '640' },
      { id: 'height', label: 'Height', value: '480' },
      { id: 'pan', label: 'Pan', value: true },
      { id: 'zoom', label: 'Zoom', value: true },
      { id: 'rotateHorizontal', label: 'Rotate (horizontal)', value: true },
      { id: 'rotateVertical', label: 'Rotate (vertical)', value: true },
      { id: 'scale', label: 'Scale', value: true },
    ],
  },
  {
    label: 'Nestle – 3D Viewer',
    value: 'nestle-3d-viewer',
    mediaTypes: [MediaType.Model3D],
    buildUrl: (url, queryParams) => `https://nest-mv.zap.works/?key=${extractFolder(url)}&${generateQueryStringFromObject(queryParams ?? {})}`,
    embedOptions: [
      { id: 'width', label: 'Width', value: '640' },
      { id: 'height', label: 'Height', value: '480' },
      { id: 'pan', label: 'Pan', value: true },
      { id: 'zoom', label: 'Zoom', value: true },
      { id: 'rotateHorizontal', label: 'Rotate (horizontal)', value: true },
      { id: 'rotateVertical', label: 'Rotate (vertical)', value: true },
      { id: 'scale', label: 'Scale', value: true },
    ],
  },
];

function buildSizeOptionsFromQuerystring(qs: EmbedQuerystring, defaults: SizeOption[]): SizeOption[] {
  return defaults.map(opt => {
    const key = opt.id as keyof EmbedQuerystring;
    if (key in qs) return { ...opt, value: qs[key] as string | boolean };
    return opt;
  });
}

function buildQuerystringFromForm(experience: string, sizeOptions: SizeOption[]): EmbedQuerystring {
  const qs: EmbedQuerystring = { experience };
  for (const opt of sizeOptions) {
    (qs as Record<string, unknown>)[opt.id] = opt.value;
  }
  return qs;
}

function buildEmbedCode(url: string, sizeOptions: SizeOption[]): string {
  const width = sizeOptions.find(o => o.id === 'width')?.value ?? '640';
  const height = sizeOptions.find(o => o.id === 'height')?.value ?? '480';
  const embedCode =  `<iframe src="${url}" sandbox="allow-scripts allow-same-origin" width="${width}" height="${height}" frameborder="0" loading="lazy" allow="camera; gyroscope; accelerometer; magnetometer; xr-spatial-tracking;"></iframe>`;
  return embedCode;
}

const PUBLIC_LINK_ERROR_MESSAGES: Record<string, string> = {
  PublicLinkAlreadyExistsErrorResponse: 'A public link already exists for this file.',
  PublicLinkFolderConflictErrorResponse: 'A public link with that slug already exists. Please choose a different slug.',
  InvalidRequestBodyResponse: 'Invalid request. Please check your input and try again.',
};

function getPublicLinkErrorMessage(err: unknown, fallback: string): string {
  const code = err && typeof err === 'object' && 'code' in err ? (err as { code: unknown }).code : undefined;
  if (typeof code === 'string' && code in PUBLIC_LINK_ERROR_MESSAGES) {
    return PUBLIC_LINK_ERROR_MESSAGES[code];
  }
  return fallback;
}

export const PublicLinkTab = ({ asset, zwApiClient, onEmbedCodeBuilt, onPublicLinkCreated, onError, havePermissionToPublish }: { asset: IAsset; zwApiClient: Client; onEmbedCodeBuilt?: (embedCode: string) => void; onPublicLinkCreated?: () => void; onError?: (message: string) => void; havePermissionToPublish?: boolean }) => {
  const { workspaceId } = useAssetActions();
  const { postSystemComment } = useEchoContext();
  const assetId = asset.getId();

  const availableTemplates = useMemo(() => {
    const mediaType = asset.getMediaType();
    return EMBED_TEMPLATES.filter(t => !t.mediaTypes || t.mediaTypes.includes(mediaType));
  }, [asset]);

  // undefined = loading, null = no link exists, IPublicLink = link exists
  const [publicLink, setPublicLink] = useState<IPublicLink | null | undefined>(undefined);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<EmbedTemplate>(availableTemplates[0]);
  const [sizeConfig, setSizeConfig] = useState<SizeOption[]>(availableTemplates.length > 0 ? availableTemplates[0].embedOptions : []);
  const [customSlug, setCustomSlug] = useState(() => slugify(asset.getName()));
  const [workspaceShortId, setWorkspaceShortId] = useState('');

  useEffect(() => {
    if (!workspaceId) return;
    zwApiClient.getWorkspace(workspaceId)
      .then(workspace => {
        setWorkspaceShortId(workspace.shortId);
      })
      .catch(() => setWorkspaceShortId(''));
  }, [zwApiClient, workspaceId]);

  const handleTemplateChange = (value: string) => {
    const template = availableTemplates.find(t => t.value === value) ?? availableTemplates[0];
    setSelectedTemplate(template);
    setSizeConfig(template.embedOptions);
  };

  useEffect(() => {
    if (!workspaceId) return;
    zwApiClient.zml.getPublicLink(workspaceId, assetId)
      .then(link => {
        setPublicLink(link);
        const qs = (link.querystring ?? {}) as EmbedQuerystring;
        const template = (qs.experience
          ? availableTemplates.find(t => t.value === qs.experience)
          : undefined) ?? availableTemplates[0];
        setSelectedTemplate(template);
        setSizeConfig(buildSizeOptionsFromQuerystring(qs, template.embedOptions));
        setIsEditing(false);
      })
      .catch(() => {
        setPublicLink(null);
      });
  }, [zwApiClient, workspaceId, assetId, availableTemplates]);

  const handleSizeConfigChange = (id: string, value: string | boolean) => {
    setSizeConfig(prev => prev.map(opt => opt.id === id ? { ...opt, value } : opt));
  };

  const handleSave = async () => {
    if (!workspaceId) return;
    setIsSaving(true);
    try {
      const querystring = buildQuerystringFromForm(selectedTemplate.value, sizeConfig);
      const createParam = { querystring, ...(customSlug.length > 0 ? { folder: customSlug } : {}) };
      const updated = publicLink
        ? await zwApiClient.zml.updatePublicLink(workspaceId, assetId, { querystring })
        : await zwApiClient.zml.createPublicLink(workspaceId, assetId, createParam);
      setPublicLink(updated);
      setSizeConfig(buildSizeOptionsFromQuerystring((updated.querystring ?? {}) as EmbedQuerystring, selectedTemplate.embedOptions));
      setIsEditing(false);
    } catch (err) {
      console.error('Failed to save public link:', err);
      onError?.(getPublicLinkErrorMessage(err, 'Failed to save public link.'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreatePublicLink = async () => {
    if (!workspaceId) return;
    setIsSaving(true);
    try {
      const querystring = buildQuerystringFromForm(selectedTemplate.value, sizeConfig);
      const createParam = { querystring, ...(customSlug.length > 0 ? { folder: customSlug } : {}) };
      const created = await zwApiClient.zml.createPublicLink(workspaceId, assetId, createParam);
      setPublicLink(created);
      setSizeConfig(buildSizeOptionsFromQuerystring((created.querystring ?? {}) as EmbedQuerystring, selectedTemplate.embedOptions));
      setIsEditing(true);
      setCustomSlug('');
      postSystemComment(assetId, publicLinkCreatedBody());
      if (onPublicLinkCreated) {
        onPublicLinkCreated();
      }
    } catch (err) {
      console.log(typeof(err));
      console.error('Failed to create public link:', typeof(err), err);
      onError?.(getPublicLinkErrorMessage(err, 'Failed to create public link.'));
    } finally {
      setIsSaving(false);
    }
  };

  const resolvedUrl = useMemo(() => {
    if (!publicLink) return '';
    return selectedTemplate.buildUrl(publicLink.url, publicLink.querystring);
  }, [publicLink, selectedTemplate]);

  const embedCode = useMemo(() => {
    if (!publicLink) return '';
    return buildEmbedCode(resolvedUrl, sizeConfig);
  }, [publicLink, resolvedUrl, sizeConfig]);

  useEffect(() => {
    if (publicLink && onEmbedCodeBuilt) {
      onEmbedCodeBuilt(embedCode);
    }
  }, [publicLink, embedCode, onEmbedCodeBuilt]);

  const isValidSlug = (slug: string) => {
    // Regex: Allows alphanumeric and hyphens
    const slugRegex = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
    return slugRegex.test(slug);
  };

  const handleSlugChange = (value: string) => {
    if (value === '' || isValidSlug(value)) {
      setCustomSlug(value);
    }
  }

  const getPublicLinkPreviewUrl = () => {
    const assetMediaType = asset.getMediaType();
    const previewFileName = assetMediaType === MediaType.Model3D ? 'model.glb' : assetMediaType;
    return (customSlug.length > 0
      ? <>https://public-media.zap.works/{workspaceShortId}/<span>{customSlug}</span>/{previewFileName}</>
      : `https://public-media.zap.works/${workspaceShortId}/${assetId}/${previewFileName}`);
  }

  if (publicLink === undefined) {
    return (
      <div className="embed-tab">
        <div className="embed-tab--empty">
          <ProcessingIcon width="var(--zpr-size-icon-default)" height="var(--zpr-size-icon-default)" />
        </div>
      </div>
    );
  }

  if (publicLink === null) {
    if (!havePermissionToPublish) {
      return (
        <div className="embed-tab flex column center">
          <p>You do not have permission to publish assets.</p>
        </div>
      );
    }

    return (
      <div className="embed-tab flex column">
        <p>Generate a public link for this asset before configuring how it is shown.</p>

        <section>
          <h4>Custom slug (optional)</h4>
          <input type="text" value={customSlug} onChange={e => setCustomSlug(e.target.value)} onBlur={() => handleSlugChange(customSlug)} className={ customSlug.length === 0 || isValidSlug(customSlug) ? '' : 'invalid'}/>

          <h4>Link preview</h4>
          <p className='public-link-preview'>
            {getPublicLinkPreviewUrl()}
          </p>

          <button
            className={`btn btn-small btn-primary w100 mt8 ${isSaving ? 'processing' : ''}`}
            onClick={handleCreatePublicLink}
            disabled={isSaving || (customSlug.length > 0 && !isValidSlug(customSlug))}
            aria-label="Create a public link for this asset"
          >
            Create public link
          </button>
        </section>

      </div>
    );
  }

  const publishedDate = publicLink?.createdAt ? new Date(publicLink.createdAt) : null;
  const toggleOptions = sizeConfig.filter(o => o.id !== 'width' && o.id !== 'height');

  return (
    <div className="embed-tab">
      <p>Easily share interactive 3D models on the web.</p>
      <div className="embed-tab-header asset-info-row">
        <h4>Settings</h4>
        {!isEditing && publicLink && havePermissionToPublish && (
          <button
            className="btn btn-small btn-link"
            onClick={() => setIsEditing(true)}
            aria-label="Edit embed settings"
          >
            Edit
          </button>
        )}
      </div>

      {isEditing && (
        <div className="embed-config-section">
          <section>
            <h4>Select template</h4>
            <select
              value={selectedTemplate.value}
              onChange={e => handleTemplateChange(e.target.value)}
              name="template-select"
              aria-label="Select template"
            >
              {availableTemplates.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </section>

          <section>
            <h4>Size</h4>
            <div className="size-config--size">
              <input
                type="number"
                value={sizeConfig.find(o => o.id === 'width')?.value.toString()}
                onChange={e => handleSizeConfigChange('width', e.target.value)}
              />
              <span>X</span>
              <input
                type="number"
                value={sizeConfig.find(o => o.id === 'height')?.value.toString()}
                onChange={e => handleSizeConfigChange('height', e.target.value)}
              />
            </div>
            {toggleOptions.length > 0 && (
              <ul className="size-config--options">
                {toggleOptions.map(opt => (
                  <li key={opt.id}>
                    <ZprToggle
                      value={String(opt.value)}
                      onChanged={(e: ZprToggleCustomEvent<string>) => handleSizeConfigChange(opt.id, e.detail === 'true')}
                      id={`${opt.id}-toggle`}
                    />
                    {opt.label && <span>{opt.label}</span>}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section>
            <button
              className="btn btn-small btn-secondary"
              onClick={handleSave}
              disabled={isSaving}
              aria-label="Save"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
            {publishedDate && <span>Last published: {formatDate(publishedDate)}</span>}
          </section>
        </div>
      )}

      {!isEditing && publicLink && (
        <div className="embed-code-section">
          <section>
            <div className="asset-info-row">
              <p>Template: {selectedTemplate.label}</p>
            </div>
            <div className="asset-info-row">
              <p>Size: {sizeConfig.find(o => o.id === 'width')?.value}x{sizeConfig.find(o => o.id === 'height')?.value}</p>
            </div>
            {publishedDate && (
              <div className="asset-info-row">
                <p>Published: {formatDate(publishedDate)}</p>
              </div>
            )}
            {toggleOptions.length > 0 && (
              <ul className="size-config--options">
                {toggleOptions.map(opt => (
                  <li key={opt.id}>
                    {opt.value ? <SuccessIcon width={16} height={16} /> : <FailIcon width={16} height={16} />} {opt.label}
                  </li>
                ))}
              </ul>
            )}
          </section>
          <section>
            <h4>Direct link</h4>
            <InlineInput
              name="direct-link"
              readOnly
              value={resolvedUrl}
              copyable
              ariaLabel="Direct link"
            />
            <h4>Embed code</h4>
            <InlineInput
              name="embed-code"
              readOnly
              value={embedCode}
              copyable
              ariaLabel="Embed code"
              isTextarea
            />
          </section>
        </div>
      )}
    </div>
  );
};
